import DownloadIcon from '@mui/icons-material/GetApp'
import { Alert, Box, Card, Chip, Typography } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import type { Identifier, RaRecord } from 'react-admin'
import {
  Button,
  Datagrid,
  downloadCSV,
  FunctionField,
  ListContextProvider,
  Pagination,
  Title,
  TopToolbar,
  useCreatePath,
  useList,
  useTranslate,
} from 'react-admin'

import { formatDay, formatServices } from '../../domain/activeInspectionCells'
import { buildActiveInspectionExport, sortActiveInspections, toCsv } from '../../domain/activeInspectionExport'
import type { ActiveInspectionFilters } from '../../domain/activeInspectionFilters'
import { applyActiveInspectionFilters, DEFAULT_ACTIVE_INSPECTION_FILTERS } from '../../domain/activeInspectionFilters'
import { useActiveInspections } from '../../hooks/useActiveInspections'
import type { ActiveInspection } from '../../models/activeInspection'
import { EvidenceStepStrip } from '../atoms/EvidenceStepStrip'
import { InspectionStateBadge } from '../atoms/InspectionStateBadge'
import { LoadingDisplay } from '../atoms/LoadingDisplay'
import { ManualInspectionMarker } from '../atoms/ManualInspectionMarker'
import { InspectionFilters } from '../molecules/InspectionFilters'

/** Page sizes offered at the bottom of the table; the table opens on the largest. */
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 200] as const

/** Name of the downloaded file, before `.csv`: the resource, as React Admin's own export names it. */
const EXPORT_FILENAME = 'inspections'

/**
 * Single table over the live `inspections` record: every location including
 * PIF, and every status including FINALIZADO and ANULADO.
 *
 * Rows are fetched once (the table is small by construction — a nightly job
 * deletes finished and cancelled inspections from previous days) and then
 * filtered, sorted and paginated in the browser, which lets the derived columns
 * — lifecycle state, evidence counts, completeness — behave like stored ones.
 */
export const InspectionList = () => {
  const translate = useTranslate()
  const [filters, setFilters] = useState<ActiveInspectionFilters>(DEFAULT_ACTIVE_INSPECTION_FILTERS)
  const { inspections, skipped, isPending, error } = useActiveInspections()

  const filtered = useMemo(() => applyActiveInspectionFilters(inspections, filters), [inspections, filters])

  const listContext = useList<ActiveInspection>({
    data: filtered,
    isPending,
    perPage: ROWS_PER_PAGE_OPTIONS[ROWS_PER_PAGE_OPTIONS.length - 1],
    sort: { field: 'day', order: 'ASC' },
    resource: 'inspections',
  })

  const { setPage, sort } = listContext
  const createPath = useCreatePath()

  // The file is the table: the same filtered array, put in the order the table
  // is sorted under, every page of it. No request is made — the rows are
  // already in the browser, and a refetch could not carry the derived columns
  // (state, manual flag, evidence counts) the operator filtered on.
  const handleExport = useCallback(() => {
    downloadCSV(toCsv(buildActiveInspectionExport(sortActiveInspections(filtered, sort), translate)), EXPORT_FILENAME)
  }, [filtered, sort, translate])

  // useList only rewinds to the first page from its own setFilters/setSort. We
  // filter upstream of it and hand it a new `data` array, which leaves `page`
  // untouched — so narrowing the results while on page 2+ would land the user on
  // a page that no longer exists and render an empty table (#61).
  const handleFiltersChange = useCallback(
    (next: ActiveInspectionFilters) => {
      setFilters(next)
      setPage(1)
    },
    [setPage],
  )

  // Every row opens a details screen. A FINALIZADO inspection has an archived
  // copy, and that copy is what its row opens; any other row opens the live
  // inspection's own screen.
  const rowClick = useCallback(
    (id: Identifier, _resource: string, record: RaRecord) => {
      const { historicalInspectionId } = record as ActiveInspection

      return historicalInspectionId
        ? createPath({ type: 'show', resource: 'historical_inspections', id: historicalInspectionId })
        : createPath({ type: 'show', resource: 'inspections', id })
    },
    [createPath],
  )

  if (error) {
    return (
      <>
        <Title title={translate('resources.inspections.name')} />
        <Alert severity="error">{translate('resources.inspections.load_error')}</Alert>
      </>
    )
  }

  return (
    <>
      <Title title={translate('resources.inspections.name')} />
      <InspectionFilters filters={filters} onChange={handleFiltersChange} />

      {skipped.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate('resources.inspections.skipped_rows', { count: skipped.length })}
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
            {skipped.map(row => (
              <li key={row.id}>
                <code>{row.id}</code> — {row.reason}
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {/*
        The same toolbar React Admin puts above the Inspecciones table, carrying
        the same control under the same label. Not the framework's ExportButton,
        which refetches through the data provider: this one writes what is on
        screen. Disabled when nothing matches — there would be nothing to write.
      */}
      <TopToolbar>
        <Button label="ra.action.export" disabled={filtered.length === 0} onClick={handleExport}>
          <DownloadIcon />
        </Button>
      </TopToolbar>

      {isPending ? (
        <LoadingDisplay />
      ) : (
        <Card>
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('resources.inspections.result_count', { count: filtered.length, total: inspections.length })}
            </Typography>
          </Box>

          <ListContextProvider value={listContext}>
            {/*
              Fecha first, then the identity of the container and what is being
              done to it, with the evidence strip last as the widest cell. The
              historical list follows the same order for the columns it shares.
            */}
            <Datagrid bulkActionButtons={false} rowClick={rowClick}>
              <FunctionField<ActiveInspection>
                source="day"
                label={translate('resources.inspections.fields.day')}
                render={record => formatDay(record.day)}
              />
              {/*
                The manual marker rides in this cell rather than a column of its
                own: the flag is binary and unremarkable on most rows, so it
                earns a glyph beside the identity it qualifies, not a full
                column. `gap` only paints between children, which keeps the
                container code at the same offset on an unmarked row.
              */}
              <FunctionField<ActiveInspection>
                source="container"
                label={translate('resources.inspections.fields.container')}
                render={record => (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <strong>{record.container}</strong>
                    {record.isLocal && <ManualInspectionMarker synced={record.seenInSostrat} />}
                  </Box>
                )}
              />
              <FunctionField<ActiveInspection>
                source="location"
                label={translate('resources.inspections.fields.location')}
                render={record => translate(`resources.historical_inspections.enums.location.${record.location}`)}
              />
              <FunctionField<ActiveInspection>
                source="inspectionLevel"
                label={translate('resources.inspections.fields.inspection_level')}
                render={record => (
                  <Chip
                    size="small"
                    label={translate(`resources.historical_inspections.enums.inspection_level_short.${record.inspectionLevel}`)}
                    title={translate(`resources.historical_inspections.enums.inspection_level.${record.inspectionLevel}`)}
                  />
                )}
              />
              <FunctionField<ActiveInspection>
                source="services"
                sortable={false}
                label={translate('resources.inspections.fields.services')}
                render={record => formatServices(record.services)}
              />
              <FunctionField<ActiveInspection>
                source="state"
                label={translate('resources.inspections.fields.state')}
                render={record => <InspectionStateBadge state={record.state} statusId={record.statusId} type={record.type} />}
              />
              <FunctionField<ActiveInspection>
                source="evidenceSteps"
                sortable={false}
                label={translate('resources.inspections.fields.steps')}
                render={record => <EvidenceStepStrip steps={record.evidenceSteps} />}
              />
            </Datagrid>
            <Pagination rowsPerPageOptions={[...ROWS_PER_PAGE_OPTIONS]} />
          </ListContextProvider>
        </Card>
      )}
    </>
  )
}
